import React, { useState, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { 
  Send, Bold, Italic, Code, Link2, List, ListOrdered, 
  Table, Quote, Loader2, Sparkles
} from 'lucide-react';

export default function ChatInput({ onSend, disabled, placeholder = "Type your message...", isProcessing }) {
  const [value, setValue] = useState('');
  const [showToolbar, setShowToolbar] = useState(false);
  const textareaRef = useRef(null);

  const insertFormatting = useCallback((before, after = '', placeholder = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const textToInsert = selectedText || placeholder;
    
    const newValue = value.substring(0, start) + before + textToInsert + after + value.substring(end);
    setValue(newValue);
    
    // Set cursor position after formatting
    setTimeout(() => {
      const newPosition = start + before.length + textToInsert.length;
      textarea.focus();
      textarea.setSelectionRange(
        selectedText ? newPosition + after.length : start + before.length,
        selectedText ? newPosition + after.length : start + before.length + placeholder.length
      );
    }, 0);
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // Keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          insertFormatting('**', '**', 'bold text');
          break;
        case 'i':
          e.preventDefault();
          insertFormatting('*', '*', 'italic text');
          break;
        case 'k':
          e.preventDefault();
          insertFormatting('[', '](url)', 'link text');
          break;
        default:
          break;
      }
    }
  };

  const handleSend = () => {
    if (value.trim() && !disabled && !isProcessing) {
      onSend(value.trim());
      setValue('');
    }
  };

  const formatButtons = [
    { icon: Bold, label: 'Bold', action: () => insertFormatting('**', '**', 'bold'), shortcut: '⌘B' },
    { icon: Italic, label: 'Italic', action: () => insertFormatting('*', '*', 'italic'), shortcut: '⌘I' },
    { icon: Code, label: 'Code', action: () => insertFormatting('`', '`', 'code') },
    { icon: Link2, label: 'Link', action: () => insertFormatting('[', '](url)', 'link text'), shortcut: '⌘K' },
    { icon: List, label: 'Bullet List', action: () => insertFormatting('\n- ', '', 'item') },
    { icon: ListOrdered, label: 'Numbered List', action: () => insertFormatting('\n1. ', '', 'item') },
    { icon: Quote, label: 'Quote', action: () => insertFormatting('\n> ', '', 'quote') },
    { icon: Table, label: 'Table', action: () => insertFormatting('\n| Header | Header |\n|--------|--------|\n| ', ' | Cell |', 'Cell') },
  ];

  return (
    <div className="border-t border-[var(--surface-border)] bg-[var(--surface-bg)]" data-testid="chat-input-container">
      {/* Formatting Toolbar */}
      {showToolbar && (
        <div className="flex items-center gap-1 px-4 py-2 border-b border-[var(--surface-border)] bg-[var(--surface-card)]/50 overflow-x-auto">
          {formatButtons.map((btn, idx) => (
            <Button
              key={idx}
              variant="ghost"
              size="sm"
              className="h-8 px-2.5 text-xs shrink-0 hover:bg-[var(--accent-indigo)]/10 hover:text-[var(--accent-indigo)]"
              onClick={btn.action}
              title={btn.shortcut ? `${btn.label} (${btn.shortcut})` : btn.label}
              disabled={disabled || isProcessing}
            >
              <btn.icon className="w-4 h-4" />
            </Button>
          ))}
          <div className="flex-1" />
          <span className="text-[10px] text-[var(--text-secondary)] shrink-0">Markdown supported</span>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4">
        <div className="relative flex items-end gap-3 bg-[var(--surface-card)] rounded-xl border border-[var(--surface-border)] focus-within:border-[var(--accent-indigo)]/50 focus-within:ring-2 focus-within:ring-[var(--accent-indigo)]/10 transition-all">
          {/* Toggle Toolbar Button */}
          <Button
            variant="ghost"
            size="sm"
            className={`absolute left-3 top-3 h-7 w-7 p-0 ${showToolbar ? 'text-[var(--accent-indigo)]' : 'text-[var(--text-secondary)]'} hover:text-[var(--accent-indigo)] hover:bg-[var(--accent-indigo)]/10`}
            onClick={() => setShowToolbar(!showToolbar)}
            title="Toggle formatting toolbar"
          >
            <Sparkles className="w-4 h-4" />
          </Button>

          {/* Textarea */}
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isProcessing}
            className="flex-1 min-h-[56px] max-h-[200px] py-3 pl-12 pr-14 text-sm bg-transparent border-0 resize-none focus:ring-0 focus-visible:ring-0 placeholder:text-[var(--text-secondary)]/50"
            rows={1}
            data-testid="chat-input"
          />

          {/* Send Button */}
          <Button
            onClick={handleSend}
            disabled={!value.trim() || disabled || isProcessing}
            className={`absolute right-3 bottom-3 h-9 w-9 p-0 rounded-lg transition-all ${
              value.trim() && !isProcessing
                ? 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white shadow-lg shadow-indigo-500/25'
                : 'bg-[var(--surface-border)] text-[var(--text-secondary)]'
            }`}
            data-testid="send-message-btn"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>

        {/* Helper text */}
        <div className="flex items-center justify-between mt-2 px-1">
          <p className="text-[10px] text-[var(--text-secondary)]">
            Press <kbd className="px-1.5 py-0.5 rounded bg-[var(--surface-card)] border border-[var(--surface-border)] text-[9px] font-mono">Enter</kbd> to send, <kbd className="px-1.5 py-0.5 rounded bg-[var(--surface-card)] border border-[var(--surface-border)] text-[9px] font-mono">Shift+Enter</kbd> for new line
          </p>
          {value.length > 0 && (
            <p className="text-[10px] text-[var(--text-secondary)]">{value.length} characters</p>
          )}
        </div>
      </div>
    </div>
  );
}
